import { Component, signal, viewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { BulkSelectPayrollEmployeeDialogComponent, ConfirmationDialogComponent } from '@app/components';
import { BulkSelectConfirmation } from '@app/components/dialogs/bulk-select-confirmation/bulk-select-confirmation.component';
import { AssignedDentalBenefitEmployee } from '@app/models/payroll/assigned-dental-benefit-employee.model';
import { DentalBenefitService } from '@app/modules/payroll/services/dental-benefit/dental-benefit.service';
import { DentalBenefitCode } from '@app/modules/payroll/services/dental-benefit/types';
import { PayrollModuleMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import { DentalBenefitsAssignedTable } from '@app/modules/payroll/services/tables/dental-benefits-assigned-table/dental-benefits-assigned.table';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { TableModule } from '@app/modules/table/table.module';
import { PlatformModule } from '@app/platform.module';
import { AuthService, NotifyService } from '@app/services';
import { TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/browser';
import { isNil } from 'lodash-es';
import { from, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

@Component({
    templateUrl: './dental-benefit-assign.template.html',
    imports: [PlatformModule, TranslateModule, TableModule, RouterModule, BulkSelectConfirmation],
    styles: [
        `
            :host {
                --segment-header-bottom-margin: 0;
                --segment-header-button-left: 0;
                --card-bottom-margin: 0;
            }
        `,
    ],
    standalone: true,
})
export class DentalBenefitAssignView {
    isLoading = false;
    dentalBenefitsAssignedTable?: DentalBenefitsAssignedTable;

    /**
     * Detach single Employee Confirmation Dialog
     */
    detachEmployeeConfirmation = viewChild.required<ConfirmationDialogComponent>('detachEmployeeConfirmation');

    /**
     * Detach/attach bulk Employees Dialog
     */
    bulkAttachDetachEmployeesDialog = viewChild.required<BulkSelectPayrollEmployeeDialogComponent<true>>(
        BulkSelectPayrollEmployeeDialogComponent
    );

    readonly benefitCode = this.route.snapshot.paramMap.get('benefit-code') as DentalBenefitCode;

    /** Used to provide context to the confirmation dialog so the employee's full name can be displayed */
    employeeToDetach: WritableSignal<string | undefined> = signal(undefined);

    constructor(
        public payrollModuleMenuService: PayrollModuleMenuService,
        private route: ActivatedRoute,
        private dentalBenefitService: DentalBenefitService,
        private notifyService: NotifyService,
        authService: AuthService
    ) {
        this.dentalBenefitsAssignedTable = new DentalBenefitsAssignedTable(authService, this.benefitCode);
    }

    /**
     * Clicking an action in the table
     */
    handleMenuItemSelected({ data, menuItem: { event } }: MenuColumnEvent<AssignedDentalBenefitEmployee>): void {
        // Currently the only action is `revoke`. This handles removing a single employee from a code
        if (event === 'revoke' && data) {
            this.employeeToDetach.set(data.fullName);
            this.detachEmployeeConfirmation()
                .open()
                .then((shouldDetach) => {
                    // If they confirm the removal in the dialog
                    if (shouldDetach && data.prEmployeeId) {
                        this.isLoading = true;
                        return this.addRemoveEmployees({
                            removals: [data.prEmployeeId.toString()],
                            additions: [],
                        }).toPromise();
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                    this.employeeToDetach.set(undefined);
                });
        }
    }

    /**
     * Launches the bulk assign dialog to remove/add employees from a code
     */
    bulkAttachDetachEmployees(): void {
        this.isLoading = true;
        // Unfortunately, we need to provide the list of all assigned employees to the code to the dialog
        // This means we need to make another request since we can't use the data from the table (it's paginated and only has the first 25 elements)
        this.dentalBenefitService
            .getAllEmployeesAssignedToDentalBenefitCode(this.benefitCode)
            .pipe(
                catchError((error) => {
                    this.handleErroredModification(error);
                    throw error;
                }),
                switchMap(
                    (existingEmployeeList) => from(this.bulkAttachDetachEmployeesDialog().show(existingEmployeeList)) // show the dialog with the existing employees in the code pre-populated
                ),
                switchMap((confirmation) => {
                    // The dialog returns null if the user doesn't do any action (ie. cancels the dialog)
                    if (isNil(confirmation)) {
                        return of(void 0);
                    }
                    const { additions, removals } = confirmation;
                    return this.addRemoveEmployees({
                        additions: additions.map((e) => e.prEmployeeId?.toString()).filter((e) => e !== undefined),
                        removals: removals.map((e) => e.prEmployeeId?.toString()).filter((e) => e !== undefined),
                    });
                })
            )
            .subscribe({
                error: () => {
                    this.isLoading = false;
                },
                complete: () => {
                    this.isLoading = false;
                },
            });
    }

    /**
     * Adds and removes employees from the code, this handles both single and bulk modifications
     */
    private addRemoveEmployees({ additions, removals }: { additions: string[]; removals: string[] }): Observable<void> {
        return this.dentalBenefitService
            .attachAndDetachEmployees(this.benefitCode, {
                attach: additions,
                detach: removals,
            })
            .pipe(
                tap(() => this.handleSuccessfulModification({ additions, removals })),
                catchError((error) => {
                    this.handleErroredModification(error);
                    return of(void 0);
                })
            );
    }

    /**
     * Renders success toast and reloads the table
     */
    private handleSuccessfulModification({ additions, removals }: { additions: string[]; removals: string[] }): void {
        // We want to know if its a single employee removal for the toast message
        const isSingleEmployeeRemoval = !additions?.length && removals?.length === 1;
        this.notifyService.success({
            key: `payroll.dental-benefits.assign.toast.success.${isSingleEmployeeRemoval ? 'removeSingle' : 'addRemoveMultiple'}`,
            params: {
                code: this.benefitCode,
                additions: additions ? additions.length : 0,
                removals: removals ? removals.length : 0,
                employee: this.employeeToDetach() ?? '',
            },
        });
        this.dentalBenefitsAssignedTable?.reload();
    }

    /**
     * Renders error toast and logs to Sentry
     */
    private handleErroredModification(error: unknown): void {
        Sentry.captureException(error);
        this.notifyService.error({
            key: 'payroll.dental-benefits.assign.toast.error',
            params: { code: this.benefitCode },
        });
    }
}
